import { useState, useCallback, useEffect } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import { getCookie, setCookie, getExpireTimeUTCStringForCookies } from '@Utilities/CookieHelper';

const LOCATION_COOKIE_CITY = 'ListingInput_LocationCity';
const LOCATION_COOKIE_STATE = 'ListingInput_LocationState';
const LOCATION_COOKIE_POSTAL = 'ListingInput_LocationPostalCode';
const LOCATION_COOKIE_COUNTRY = 'ListingInput_LocationCountry';
const LOCATION_COOKIE_DISPLAY = 'ListingInput_LocationDisplay';
const LOCATION_COOKIE_ADDRESS = 'ListingInput_LocationAddress';
const LOCATION_COOKIE_STATE_SHORT_NAME = 'ListingInput_LocationStateShortName';
const LOCATION_COOKIE_COUNTRY_SHORT_NAME = 'ListingInput_LocationCountryShortName';
const COOKIE_EXPIRY_DAYS = 7;

const getLocationFromCookie = () => {
    const city = getCookie(LOCATION_COOKIE_CITY);
    const state = getCookie(LOCATION_COOKIE_STATE);
    const postalCode = getCookie(LOCATION_COOKIE_POSTAL);
    const country = getCookie(LOCATION_COOKIE_COUNTRY);
    const displayText = getCookie(LOCATION_COOKIE_DISPLAY);
    const address = getCookie(LOCATION_COOKIE_ADDRESS);
    const stateShortName = getCookie(LOCATION_COOKIE_STATE_SHORT_NAME);
    const countryShortName = getCookie(LOCATION_COOKIE_COUNTRY_SHORT_NAME);

    if (displayText) {
        return { Address: address || '', City: city || '', State: state || '', PostalCode: postalCode || '', Country: country || '', DisplayText: displayText };
    }
    return null;
};

const clearLocationCookies = () => {
    const expired = 'Thu, 01 Jan 1970 00:00:00 UTC';
    setCookie(LOCATION_COOKIE_CITY, '', expired);
    setCookie(LOCATION_COOKIE_STATE, '', expired);
    setCookie(LOCATION_COOKIE_POSTAL, '', expired);
    setCookie(LOCATION_COOKIE_COUNTRY, '', expired);
    setCookie(LOCATION_COOKIE_DISPLAY, '', expired);
    setCookie(LOCATION_COOKIE_ADDRESS, '', expired);
    setCookie(LOCATION_COOKIE_STATE_SHORT_NAME, '', expired);
    setCookie(LOCATION_COOKIE_COUNTRY_SHORT_NAME, '', expired);
};

const setLocationCookie = (location) => {
    clearLocationCookies()
    const expiresOn = getExpireTimeUTCStringForCookies(COOKIE_EXPIRY_DAYS);
    setCookie(LOCATION_COOKIE_CITY, location.City, expiresOn);
    setCookie(LOCATION_COOKIE_STATE, location.State, expiresOn);
    setCookie(LOCATION_COOKIE_POSTAL, location.PostalCode, expiresOn);
    setCookie(LOCATION_COOKIE_COUNTRY, location.Country, expiresOn);
    setCookie(LOCATION_COOKIE_DISPLAY, location.DisplayText, expiresOn);
    setCookie(LOCATION_COOKIE_ADDRESS, location.Address || '', expiresOn);
    setCookie(LOCATION_COOKIE_STATE_SHORT_NAME, location.StateShortName || '', expiresOn);
    setCookie(LOCATION_COOKIE_COUNTRY_SHORT_NAME, location.CountryShortName || '', expiresOn);
};

const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
    });
};

const LocationPicker = (props) => {
    const [locationText, setLocationText] = useState(props.value || '');
    const [isLocating, setIsLocating] = useState(false);
    const [showGeoModal, setShowGeoModal] = useState(false);
    const [geoError, setGeoError] = useState('');

    const notifyLocationSelected = useCallback((location) => {
        setLocationCookie(location);
        setGeoError('');
        if (props.onLocationSelected) {
            props.onLocationSelected(location);
        }
    }, [props.onLocationSelected]);

    useEffect(() => {
        if (!locationText) {
            const cookieLocation = getLocationFromCookie();
            if (cookieLocation) {
                setLocationText(cookieLocation.DisplayText);
                if (props.onLocationSelected) {
                    props.onLocationSelected(cookieLocation);
                }
            }
        }
    }, []);

    useEffect(() => {
        if (props.value !== undefined && props.value !== null) {
            setLocationText(props.value);
        }
    }, [props.value]);

    const stripDiacritics = (str) => {
        return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : str;
    };

    const extractAddressComponents = (addressComponents) => {
        let city = '';
        let state = '';
        let stateShortName = '';
        let postalCode = '';
        let country = '';
        let countryShortName = '';

        if (addressComponents != null && typeof addressComponents[Symbol.iterator] === 'function') {
            for (const component of addressComponents) {
                const componentType = component.types[0];

                switch (componentType) {
                    case 'locality':
                    case 'postal_town': {
                        city = stripDiacritics(component.long_name);
                        break;
                    }
                    case 'administrative_area_level_1': {
                        state = stripDiacritics(component.long_name);
                        stateShortName = component.short_name ? component.short_name.replace(/\./g, '') : '';
                        break;
                    }
                    case 'postal_code': {
                        postalCode = component.long_name;
                        break;
                    }
                    case 'country': {
                        country = component.short_name === 'US' ? 'USA' : component.long_name;
                        countryShortName = component.short_name || '';
                        break;
                    }
                }
            }
        }

        return { city, state, stateShortName, postalCode, country, countryShortName };
    };

    const finalizeLocation = useCallback((city, state, postalCode, country, address, stateShortName, countryShortName) => {
        const displayText = [city, state, postalCode, country].filter(Boolean).join(', ');
        setLocationText(displayText);

        notifyLocationSelected({
            Address: address || '',
            City: city,
            State: state,
            StateShortName: stateShortName || '',
            CountryShortName: countryShortName || '',
            PostalCode: postalCode,
            Country: country,
            DisplayText: displayText
        });
    }, [notifyLocationSelected]);

    const { ref } = usePlacesWidget({
        onPlaceSelected: (place) => {
            const { city, state, stateShortName, postalCode, country, countryShortName } = extractAddressComponents(place.address_components);
            finalizeLocation(city, state, postalCode, country, '', stateShortName, countryShortName);
        },
        options: {
            types: ['(regions)'],
            fields: ['address_components', 'place_id']
        }
    });

    const handleInputChange = useCallback((e) => {
        setLocationText(e.target.value);
        setGeoError('');
        if (props.onFieldChanged) {
            props.onFieldChanged(e.target.value);
        }
    }, [props.onFieldChanged]);

    const handleGeoFailure = useCallback(() => {
        setIsLocating(false);
        setShowGeoModal(false);
        setGeoError(props.geoErrorText || 'We could not retrieve your location');
    }, [props.geoErrorText]);

    const handleGeolocation = useCallback(() => {
        if (!navigator.geolocation) {
            handleGeoFailure();
            return;
        }

        setIsLocating(true);
        setGeoError('');

        getCurrentPosition()
            .then((position) => {
                const xhr = new XMLHttpRequest();
                xhr.onload = () => {
                    if (xhr.status === 200) {
                        const response = JSON.parse(xhr.response);
                        const loc = response.oLocationViewModel;

                        if (loc && (loc.City || loc.State || loc.PostalCode || loc.Country)) {
                            const displayText = [loc.City, loc.State, loc.PostalCode, loc.Country].filter(Boolean).join(', ');
                            setLocationText(displayText);
                            setIsLocating(false);
                            setShowGeoModal(false);

                            notifyLocationSelected({
                                Address: loc.Address || '',
                                City: loc.City || '',
                                State: loc.State || '',
                                PostalCode: loc.PostalCode || '',
                                Country: loc.Country || '',
                                DisplayText: displayText
                            });
                        } else {
                            handleGeoFailure();
                        }
                    } else {
                        handleGeoFailure();
                    }
                };
                xhr.onerror = () => {
                    handleGeoFailure();
                };

                xhr.open("POST", '/listinginput/Location/ReverseGeocode', true);
                xhr.setRequestHeader("Content-type", "application/json");
                xhr.send(JSON.stringify({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                }));
            })
            .catch(() => {
                handleGeoFailure();
            });
    }, [notifyLocationSelected, handleGeoFailure]);

    const hasError = props.error || geoError;
    const inputClassName = hasError ? 'inputError' : '';

    const validationError = hasError ? (
        <p style={{ margin: '0' }} className="validationError">
            <span dangerouslySetInnerHTML={{ __html: geoError || props.errorText }}></span>
        </p>
    ) : null;

    return (
        <div className="location-picker" key="LocationPicker">
            <div className="location-picker-input">
                <input
                    type="text"
                    id={props.id}
                    ref={ref}
                    className={inputClassName}
                    value={locationText}
                    onChange={handleInputChange}
                    onBlur={props.onBlur}
                    placeholder={props.placeholder || ''}
                    aria-labelledby={props.ariaLabelledBy}
                    autoComplete="one-time-code" // using "one-time-code" to disable autocomplete dropdown from obstructing the Google autocomplete
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.keyCode === 13) {
                            event.preventDefault();
                        }
                    }}
                />
                <button
                    type="button"
                    className="current-loc-btn PrimaryBGC siteFontColor"
                    aria-label={props.geolocationLabel || 'Use My Current Location'}
                    onClick={() => {setShowGeoModal(true); $("#location-geo-modal-btn").focus();}}
                >
                    <i className="fas fa-crosshairs" aria-hidden="true"></i>
                </button>
            </div>
            {validationError}
            {showGeoModal && (
                <div className="ts-modal-container location-geo-modal" onClick={(e) => { if (e.target === e.currentTarget && !isLocating) setShowGeoModal(false); }}>
                    <div className="ts-modal-content location-geo-modal-content">
                        {!isLocating && (
                            <button type="button" className="location-geo-modal-close PrimaryBGC siteFontColor" aria-label="Close" onClick={() => setShowGeoModal(false)}>
                                <i className="fas fa-times" aria-hidden="true"></i>
                            </button>
                        )}
                        <button
                            type="button"
                            id="location-geo-modal-btn"
                            className="PrimaryBGC siteFontColor location-geo-modal-btn"
                            onClick={handleGeolocation}
                            disabled={isLocating}
                        >
                            {isLocating
                                ? <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                                : <i className="fas fa-crosshairs" aria-hidden="true"></i>
                            }
                            {props.geolocationLabel || 'Use My Current Location'}
                        </button>
                        {props.geolocationHint && (
                            <p className="location-geo-modal-hint">
                                <i className="fas fa-info-circle" aria-hidden="true"></i> {props.geolocationHint}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationPicker;
global.LocationPicker = LocationPicker;
